import React, { useRef, useState } from 'react';
import { ILot } from '../../types/IOrderResponse';
import {
  formatDateOnly,
  formatOrderDateTime,
} from '../../utils/formatOrderDate';

interface LotsEditorProps {
  kitLots: ILot[];
  boxLots: ILot[];
  onKitLotsChange: (lots: ILot[]) => void;
  onBoxLotsChange: (lots: ILot[]) => void;
  kitQuantity?: number;
  boxQuantity?: number;
}

// Hover text for a lot row: who added it and when, as far as the API tells us.
const lotProvenance = (lot: ILot) => {
  if (lot.id == null) return 'Not saved yet';
  const who = lot.modifiedBy?.name;
  const when = formatOrderDateTime(lot.dateModified);
  if (who && when) return `Added by ${who} on ${when}`;
  if (who) return `Added by ${who}`;
  return when ? `Added ${when}` : undefined;
};

interface LotSectionProps {
  title: string;
  lots: ILot[];
  onChange: (lots: ILot[]) => void;
  orderQuantity?: number;
}

/**
 * One group of lots as a table, with an always-present entry row at the bottom.
 * Nothing slides in: type a lot number, adjust the prefilled quantity if needed,
 * pick an expiry and press Enter (or Add). A lot that is not saved yet stays
 * editable in place, so a typo is fixed where it is; saved lots are read-only.
 */
const LotSection: React.FC<LotSectionProps> = ({
  title,
  lots,
  onChange,
  orderQuantity,
}) => {
  const allocated = lots.reduce(
    (sum, lot) => sum + (Number(lot.quantity) || 0),
    0,
  );
  const remaining =
    orderQuantity !== undefined ? orderQuantity - allocated : undefined;
  const over = remaining !== undefined && remaining < 0;

  // Entry-row draft. Quantity stays null until the user types one, so it keeps
  // tracking whatever is still unallocated as lots are added and removed.
  const [draftQty, setDraftQty] = useState<string | null>(null);
  const [draftLot, setDraftLot] = useState('');
  const [draftExpiry, setDraftExpiry] = useState('');
  const lotInputRef = useRef<HTMLInputElement>(null);

  const qtyValue =
    draftQty ??
    (remaining !== undefined && remaining > 0 ? String(remaining) : '');
  const qtyNumber = Number(qtyValue) || 0;
  const canAdd = qtyNumber > 0 && draftLot.trim().length > 0;

  const addLot = () => {
    if (!canAdd) return;
    onChange([
      ...lots,
      { quantity: qtyNumber, lotNumber: draftLot.trim(), expiry: draftExpiry },
    ]);
    setDraftQty(null);
    setDraftLot('');
    setDraftExpiry('');
    lotInputRef.current?.focus();
  };

  const removeLot = (index: number) =>
    onChange(lots.filter((_, i) => i !== index));

  const updateLot = (index: number, patch: Partial<ILot>) =>
    onChange(lots.map((lot, i) => (i === index ? { ...lot, ...patch } : lot)));

  const onEntryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addLot();
    }
  };

  const pct = orderQuantity
    ? Math.min(100, (allocated / orderQuantity) * 100)
    : 0;

  return (
    <div className="lots-section">
      <div className="lots-section-header">
        <h5>{title}</h5>
        {orderQuantity !== undefined ? (
          <span
            className={`lots-allocated${over ? ' lots-allocated--over' : ''}`}
          >
            <b>{allocated}</b> of {orderQuantity} allocated
            {remaining !== undefined && remaining > 0
              ? ` · ${remaining} remaining`
              : ''}
            {remaining !== undefined && remaining < 0
              ? ` · ${-remaining} over`
              : ''}
          </span>
        ) : null}
      </div>
      {orderQuantity !== undefined ? (
        <div className={`lots-bar${over ? ' lots-bar--over' : ''}`}>
          <i style={{ width: `${pct}%` }} />
        </div>
      ) : null}

      <table className="lots-table">
        <thead>
          <tr>
            <th className="lots-col-qty">Qty</th>
            <th>Lot number</th>
            <th className="lots-col-exp">Expiry</th>
            <th className="lots-col-act">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {lots.map((lot, index) => (
            <tr
              // eslint-disable-next-line react/no-array-index-key
              key={lot.id ?? `new-${index}`}
              className={lot.id == null ? 'lots-row--new' : undefined}
              title={lotProvenance(lot)}
            >
              {lot.id == null ? (
                <>
                  <td className="lots-col-qty">
                    <span className="lots-qty-edit">
                      <span className="lots-new-dot" title="Not saved yet" />
                      <input
                        aria-label={`${title} lot ${index + 1} quantity`}
                        type="number"
                        min="1"
                        value={lot.quantity || ''}
                        onChange={(e) =>
                          updateLot(index, {
                            quantity: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </span>
                  </td>
                  <td>
                    <input
                      aria-label={`${title} lot ${index + 1} lot number`}
                      type="text"
                      autoComplete="off"
                      value={lot.lotNumber ?? ''}
                      onChange={(e) =>
                        updateLot(index, { lotNumber: e.target.value })
                      }
                    />
                  </td>
                  <td className="lots-col-exp">
                    <input
                      aria-label={`${title} lot ${index + 1} expiry`}
                      type="date"
                      value={lot.expiry ?? ''}
                      onChange={(e) =>
                        updateLot(index, { expiry: e.target.value })
                      }
                    />
                  </td>
                </>
              ) : (
                <>
                  <td className="lots-col-qty">{lot.quantity}</td>
                  <td>{lot.lotNumber || '—'}</td>
                  <td className="lots-col-exp">
                    {formatDateOnly(lot.expiry) || '—'}
                  </td>
                </>
              )}
              <td className="lots-col-act">
                <button
                  type="button"
                  className="lots-remove"
                  onClick={() => removeLot(index)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
          <tr className="lots-entry">
            <td className="lots-col-qty">
              <input
                aria-label={`${title} quantity`}
                type="number"
                min="1"
                placeholder="0"
                value={qtyValue}
                onChange={(e) => setDraftQty(e.target.value)}
                onKeyDown={onEntryKeyDown}
              />
            </td>
            <td>
              <input
                ref={lotInputRef}
                aria-label={`${title} lot number`}
                type="text"
                autoComplete="off"
                placeholder="Lot number"
                value={draftLot}
                onChange={(e) => setDraftLot(e.target.value)}
                onKeyDown={onEntryKeyDown}
              />
            </td>
            <td className="lots-col-exp">
              <input
                aria-label={`${title} expiry`}
                type="date"
                value={draftExpiry}
                onChange={(e) => setDraftExpiry(e.target.value)}
                onKeyDown={onEntryKeyDown}
              />
            </td>
            <td className="lots-col-act">
              <button
                type="button"
                className="lots-add"
                onClick={addLot}
                disabled={!canAdd}
                title={canAdd ? undefined : 'Enter a quantity and lot number'}
              >
                Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const LotsEditor: React.FC<LotsEditorProps> = ({
  kitLots,
  boxLots,
  onKitLotsChange,
  onBoxLotsChange,
  kitQuantity,
  boxQuantity,
}) => (
  <div className="lots-editor">
    <LotSection
      title="Kit lots"
      lots={kitLots}
      onChange={onKitLotsChange}
      orderQuantity={kitQuantity}
    />
    <LotSection
      title="Box lots"
      lots={boxLots}
      onChange={onBoxLotsChange}
      orderQuantity={boxQuantity}
    />
  </div>
);

export default LotsEditor;
